/**
 * On a mould's page: the spreadsheet imports that brought it in or changed it,
 * newest first - so any mould can be traced to the file it came from.
 *
 * Online only (Import History lives on the server); with no connection, or no
 * imports, it shows nothing rather than an empty box.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileSpreadsheet } from 'lucide-react';
import type { MouldImportRef } from '@mouldtrack/shared';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '../../components/ui/primitives.js';
import { listMouldImports } from '../../api/client.js';
import { useCan } from '../../auth/AuthProvider.js';
import { formatDateTime } from '../../lib/format.js';
import { RECORD_ACTION } from './importUi.js';

export function MouldImports({ mouldId }: { mouldId: string }) {
  const [refs, setRefs] = useState<MouldImportRef[] | null>(null);
  // Import History itself is for the people who import.
  const canOpen = useCan('create', 'moulds');

  useEffect(() => {
    let cancelled = false;
    listMouldImports(mouldId)
      .then((r) => !cancelled && setRefs(r))
      .catch(() => !cancelled && setRefs([]));
    return () => {
      cancelled = true;
    };
  }, [mouldId]);

  if (!refs?.length) return null;
  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="size-4 text-success" aria-hidden /> Imported from
        </CardTitle>
      </CardHeader>
      <CardBody>
        <ul className="flex flex-col divide-y divide-border">
          {refs.map((r) => (
            <li key={`${r.runId}-${r.sheetRow ?? ''}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm first:pt-0 last:pb-0">
              <Badge tone={RECORD_ACTION[r.action].tone}>{RECORD_ACTION[r.action].label}</Badge>
              {canOpen ? (
                <Link to={`/imports/${r.runId}`} className="font-medium text-primary hover:underline">
                  {r.fileName}
                </Link>
              ) : (
                <span className="font-medium">{r.fileName}</span>
              )}
              <span className="text-muted-foreground">
                {r.sheetRow != null ? `row ${r.sheetRow}${r.sheet ? ` of ${r.sheet}` : ''} · ` : ''}
                {formatDateTime(r.startedAt)}
                {r.userName ? ` by ${r.userName}` : ''}
              </span>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}
